/**
 * ProductHero component provides detailed description about the selected product and offers applicable.
 *
 * @module views/components/ProductHero
 * @memberof -Common
 */
import './ProductHero.scss';

import React, { useCallback, useEffect } from 'react';

import PropTypes from 'prop-types';

import AsyncComponent from '@ulta/core/components/AsyncComponent/AsyncComponent';
import { useAppConfigContext } from '@ulta/core/providers/AppConfigProvider/AppConfigProvider';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { getStorage, setStorage } from '@ulta/core/utils/storage/storage';
import { STORAGE_KEY } from '@ulta/core/utils/storageKeys/storageKeys';

import DynamicPrompts from '../DynamicPrompts/DynamicPrompts';
import MediaGallery from '../MediaGallery/MediaGallery';
import ProductDetail from '../ProductDetail/ProductDetail';
import ProductInformation from '../ProductInformation/ProductInformation';
import ProductSummary from '../ProductSummary/ProductSummary';
import * as utils from './ProductHero';

/**
 * Represents a ProductHero component
 *
 * @method
 * @param {ProductHeroProps} props - React properties passed from composition
 * @returns ProductHero
 */
export const ProductHero = function( props ){
  const { componentKey, modules = [] } = props;

  const productInformation = modules.find(
    ( module ) => module.type === 'ProductInformation'
  );
  const socialTags = modules.find(
    ( module ) => module.type === 'SocialTags'
  );
  const productPricing = modules.find(
    ( module ) => module.type === 'ProductPricing'
  );
  const mediaGallery = modules.find(
    ( module ) => module.type === 'MediaGallery'
  );
  const productVariant = modules.find(
    ( module ) => module.type === 'ProductVariant'
  );
  const productSummary = modules.find(
    ( module ) => module.type === 'ProductSummary'
  );
  const productDetail = modules.find(
    ( module ) => module.type === 'ProductDetail'
  );
  const adBugOffersRail = modules.find(
    ( module ) => module.type === 'AdBugOffersRail'
  );
  const productActions = modules.find(
    ( module ) => module.type === 'ProductActions'
  );
  const productActionsV2 = modules.find(
    ( module ) => module.type === 'ProductActionsV2'
  );
  const rewardsMessaging = modules.find(
    ( module ) => module.type === 'RewardsMessaging'
  );
  const boughtTogether = modules.find(
    ( module ) => module.type === 'BoughtTogether'
  );
  const routineBuilder = modules.find(
    ( module ) => module.type === 'routineBuilder'
  );

  const dynamicPrompts = modules.find(
    ( module ) => module.type === 'DynamicPrompts'
  );


  const displayProductName = utils.getDisplayProductName( { productInformation, productVariant } );

  const verticalSlot = modules.filter( ( module ) => module.type === 'VerticalSlot' );

  const verticalSlotFirst = verticalSlot?.[0];
  const verticalSlotSecond = verticalSlot?.[1];

  const personalizationContent = modules.filter(
    ( module ) => module.type === 'PersonalizationContent'
  );

  const personalizationContentFirst = personalizationContent?.[0];
  const personalizationContentSecond = personalizationContent?.[1];

  const { recentlyViewedLimit, recentlyViewedForAutoSuggestionLimit } = useAppConfigContext();

  const { inflection } = useDeviceInflection();

  const displayVariants =
    productVariant &&
    ( productVariant.dimensionsLabel !== null ||
      productVariant.typeLabel !== null );

  useEffect( () => {
    handleRecentlyViewedSKU();
  }, [productVariant] );

  const handleRecentlyViewedSKU = useCallback(
    utils.composeRecentlyViewedSKU(
      {
        productVariant,
        ultaRecentlyViewedKey: STORAGE_KEY.recentlyViewedSKUs,
        recentlyViewedLimit,
        recentlyViewedSKUsForAutoSuggestionKey: STORAGE_KEY.recentlyViewedSKUsForAutoSuggestion,
        recentlyViewedForAutoSuggestionLimit,
        productPricing
      },
      { getStorage, setStorage }
    ),
    [productVariant, productPricing]
  );
  return (
    <div id={ props.id }
      className='ProductHero'
    >
      <div className='ProductHero__content'>
        { mediaGallery && (
          <div className='ProductHero__MediaGallery'>
            <MediaGallery
              id={ mediaGallery.id }
              ssr={ props.ssr }
              { ...spreadProps( {
                ...mediaGallery,
                deviceType: props.ssr && props.deviceType
              } ) }
              nextAccessibility={ mediaGallery.nextAccessibility.label }
              previousAccessibility={ mediaGallery.previousAccessibility.label }
              defaultImage={ mediaGallery.items[0] }
              items={ mediaGallery.items }
              tryItAction={ mediaGallery.tryItAction }
              ratio={ '1:1' }
              carouselAccessibility={ mediaGallery.carouselAccessibility }
              carouselDetailsAccessibility={
                mediaGallery.carouselDetailsAccessibility
              }
              carouselCardAccessibility={ mediaGallery.carouselCardAccessibility }
              componentKey={ componentKey }
              nextClickAction={ mediaGallery.nextAccessibility }
              previousClickAction={ mediaGallery.previousAccessibility }
              zoomLegendAccessibilityLabel={
                mediaGallery.zoomLegendAccessibilityLabel
              }
              zoomPlusIconAccessibilityLabel={
                mediaGallery.zoomPlusIconAccessibilityLabel
              }
              zoomMinusIconAccessibilityLabel={
                mediaGallery.zoomMinusIconAccessibilityLabel
              }
              moveUpIconAccessibilityLabel={
                mediaGallery.moveUpIconAccessibilityLabel
              }
              moveDownIconAccessibilityLabel={
                mediaGallery.moveDownIconAccessibilityLabel
              }
              moveLeftIconAccessibilityLabel={
                mediaGallery.moveLeftIconAccessibilityLabel
              }
              moveRightIconAccessibilityLabel={
                mediaGallery.moveRightIconAccessibilityLabel
              }
              screenReaderAccessibilityLabel={
                mediaGallery.screenReaderAccessibilityLabel
              }
              moveLegendAccessibilityLabel={
                mediaGallery.moveLegendAccessibilityLabel
              }
            />
          </div>
        ) }
        { productInformation && (
          <ProductInformation
            id={ productInformation.id }
            brandAction={ productInformation.brandAction }
            dataCapture={ productInformation.dataCapture }
            productName={ displayProductName }
            productId={ productPricing.productId }
            rating={ productInformation.rating }
            ratingAction={ productInformation.ratingAction }
            reviewCount={ productInformation.reviewCount }
            reviewsCountLabel={ productInformation.reviewsCountLabel }
            reviewable={ productInformation.reviewable }
            badges={ productInformation.badges }
            writeReviewAction={ productInformation.writeReviewAction }
            askQuestionAction={ productInformation.askQuestionAction }
            itemLabel={ productInformation.itemLabel }
            qAndAAction={ productInformation.qAndAAction }
            reviewAccessibilityLabel={
              productInformation.reviewAccessibilityLabel
            }
            meta={ productInformation.meta }
            socialTags={ socialTags }
            { ...spreadProps( { ...productInformation } ) }
          />
        ) }

        { productPricing && (
          <AsyncComponent
            moduleName='ProductPricing'
            priceLabel={ productPricing.priceLabel }
            listPrice={ productPricing.listPrice }
            originalPriceLabel={ productPricing.originalPriceLabel }
            listPriceAccessibilityLabel={
              productPricing.listPriceAccessibilityLabel
            }
            salePrice={ productPricing.salePrice }
            salePriceAccessibilityLabel={
              productPricing.salePriceAccessibilityLabel
            }
            kitPrice={ productPricing.kitPrice }
            message={ productPricing.message }
            productId={ productPricing.productId }
            skuId={ productPricing.skuId }
            id={ productPricing.id }
            appDownloadLabel={ productPricing.appDownloadLabel }
            appDownloadAction={ productPricing.appDownloadAction }
            meta={ productPricing.meta }
            { ...spreadProps( { ...productPricing } ) }
          />
        ) }

        { displayVariants && (
          <AsyncComponent
            moduleName='ProductVariant'
            { ...spreadProps( { ...productVariant } ) }
            skuId={ props.skuId }
          />
        ) }

        { adBugOffersRail && (
          <AsyncComponent
            moduleName='AdBugOffersRail'
            { ...spreadProps( { ...adBugOffersRail } ) }
          />
        ) }

        { productActions && (
          <AsyncComponent
            { ...spreadProps( { ...productActions } ) }
            moduleName='ProductActions'
          />
        ) }

        { productActionsV2 && (
          <AsyncComponent
            { ...spreadProps( { ...productActionsV2 } ) }
            moduleName='ProductActionsV2'
          />
        ) }

        { verticalSlotFirst && (
          <AsyncComponent
            moduleName='VerticalSlot'
            configureContainerQuery={ props.configureContainerQuery }
            invokeMutation={ props.invokeMutation }
            pageRenderTimestamp={ props.pageRenderTimestamp }
            displayAsMobile={ true }
            { ...spreadProps( { ...verticalSlotFirst } ) }
          />
        ) }
        { personalizationContentFirst && (
          <AsyncComponent
            moduleName='PersonalizationContent'
            invokeMutation={ props.invokeMutation }
            pageRenderTimestamp={ props.pageRenderTimestamp }
            { ...spreadProps( { ...personalizationContentFirst } ) }
          />
        ) }

        { rewardsMessaging && (
          <AsyncComponent
            moduleName='RewardsMessaging'
            configureContainerQuery={ props.configureContainerQuery }
            invokeMutation={ props.invokeMutation }
            pageRenderTimestamp={ props.pageRenderTimestamp }
            displayAsMobile={ true }
            { ...spreadProps( { ...rewardsMessaging } ) }
          />
        ) }

        { productSummary && (
          <ProductSummary
            id={ productSummary.id }
            title={ productSummary.title }
            message={ productSummary.message }
            summaryCards={ productSummary.summaryCards }
            variant={ productSummary.variant || 'Half' }
            layout={ inflection.DESKTOP ? 'horizontal' : 'vertical' }
          />
        ) }

        { productDetail && (
          <ProductDetail
            id={ productDetail.id }
            { ...spreadProps( { ...productDetail } ) }
          />
        ) }

        { dynamicPrompts && (
          <DynamicPrompts
            id={ dynamicPrompts.id }
            { ...spreadProps( { ...dynamicPrompts } ) }
          />
        ) }



        { boughtTogether && (
          <AsyncComponent
            { ...spreadProps( { ...boughtTogether } ) }
            moduleName='BoughtTogether'
          />
        ) }

        { routineBuilder && (
          <AsyncComponent
            { ...spreadProps( { ...routineBuilder } ) }
            moduleName='BoughtTogether'
          />
        ) }

        { verticalSlotSecond && (
          <AsyncComponent
            moduleName='VerticalSlot'
            configureContainerQuery={ props.configureContainerQuery }
            invokeMutation={ props.invokeMutation }
            pageRenderTimestamp={ props.pageRenderTimestamp }
            displayAsMobile={ true }
            { ...spreadProps( { ...verticalSlotSecond } ) }
          />
        ) }

        { personalizationContentSecond && (
          <AsyncComponent
            moduleName='PersonalizationContent'
            invokeMutation={ props.invokeMutation }
            pageRenderTimestamp={ props.pageRenderTimestamp }
            { ...spreadProps( { ...personalizationContentSecond } ) }
          />
        ) }
      </div>
    </div>
  );
};

/**
   * @method getDisplayProductName
   * @summary This method will return the current variant selected
   * @param {productVariant} data.productVariant - product variant array
   * @param {productInformation} data.productInformation - product information object
   */

export const getDisplayProductName = ( data ) => {
  const { productInformation, productVariant } = data || {};
  const selectedSku =  productVariant?.variants?.find(
    ( variant ) => variant.selected === true
  );
  if( selectedSku?.name ){
    return productInformation?.productName + ' - ' + selectedSku.name;
  }
  return productInformation?.productName;
};

/**
 * Updates the recently viewed SKU list in storage and maintains an auto-suggestion list if applicable.
 *
 * @param {Object} data - The configuration data.
 * @param {Object} data.productVariant - The product variant information.
 * @param {Array} data.productVariant.variants - List of product variants.
 * @param {string} data.ultaRecentlyViewedKey - Storage key for recently viewed items.
 * @param {number} data.recentlyViewedLimit - Maximum number of items in the recently viewed list.
 * @param {number} data.recentlyViewedForAutoSuggestionLimit - Maximum number of items in the auto-suggestion list.
 * @param {string} [data.recentlyViewedSKUsForAutoSuggestionKey] - Storage key for auto-suggestion SKUs (optional).
 * @param {Object} data.productPricing - Product pricing information.
 * @param {string} data.productPricing.productId - The product ID associated with the selected variant.
 *
 * @param {Object} methods - Methods for interacting with storage.
 * @param {Function} methods.getStorage - Function to retrieve stored data.
 * @param {Function} methods.setStorage - Function to store data.
 *
 * @returns {Function} A function that updates the recently viewed SKU lists.
 */
export const composeRecentlyViewedSKU = ( data, methods ) => () => {
  const {
    productVariant,
    ultaRecentlyViewedKey,
    recentlyViewedLimit,
    recentlyViewedForAutoSuggestionLimit,
    recentlyViewedSKUsForAutoSuggestionKey,
    productPricing
  } = data || {};
  const { getStorage, setStorage } = methods || {};

  if( !getStorage ||
    !setStorage ||
    !productVariant ||
    !ultaRecentlyViewedKey ){
    return;
  }

  let recentlyViewed =
    getStorage( {
      secure: false,
      key: ultaRecentlyViewedKey
    } )?.recentlyViewed || [];

  const selectedSku = productVariant.variants?.find(
    ( variant ) => variant.selected === true
  );

  const selectedProductId = productPricing?.productId;
  const selectedVariantSkuId = selectedSku?.skuId;
  const selectedProductSkuKey = `${selectedProductId}::${selectedVariantSkuId}`;

  recentlyViewed = recentlyViewed?.filter( ( recentlyViewedItem ) => {
    const recentlyViewedProductId = recentlyViewedItem?.split( '::' )?.[0];

    return recentlyViewedProductId !== selectedProductId;
  } );

  recentlyViewed.unshift( selectedProductSkuKey );

  if( recentlyViewed.length > recentlyViewedLimit ){
    recentlyViewed.pop();
  }

  setStorage( {
    secure: false,
    key: ultaRecentlyViewedKey,
    value: { recentlyViewed }
  } );

  if( recentlyViewedSKUsForAutoSuggestionKey ){
    let recentlyViewedForAutoSuggest =
      getStorage( {
        secure: false,
        key: recentlyViewedSKUsForAutoSuggestionKey
      } )?.recentlyViewedForAutoSuggest || [];

    recentlyViewedForAutoSuggest = recentlyViewedForAutoSuggest?.filter( ( recentlyViewedItem ) => {
      const recentlyViewedProductId = recentlyViewedItem?.split( '::' )?.[0];

      return recentlyViewedProductId !== selectedProductId;
    } );

    recentlyViewedForAutoSuggest.unshift( selectedProductSkuKey );

    if( recentlyViewedForAutoSuggest.length > recentlyViewedForAutoSuggestionLimit ){
      recentlyViewedForAutoSuggest = recentlyViewed?.slice( 0, recentlyViewedForAutoSuggestionLimit );
    }

    setStorage( {
      secure: false,
      key: recentlyViewedSKUsForAutoSuggestionKey,
      value: { recentlyViewedForAutoSuggest }
    } );
  }
};

/**
 * Property type definitions
 *
 * @typedef ProductHeroProps
 * @type {object}
 * @property {object} modules - Array of modules required for ProductHero organism
 * @property {string} deviceType - Sets value for the deviceType
 * @property {boolean} ssr - Sets value for the ssr
 * @property {string} id - Sets id for the ProductHero
 */
export const propTypes = {
  modules: PropTypes.arrayOf(
    PropTypes.shape( {
      /** Properties for ProductInformation */
      id: PropTypes.string,
      type: PropTypes.string,
      brandAction: PropTypes.object,
      productName: PropTypes.string,
      rating: PropTypes.number,
      ratingAction: PropTypes.object,
      reviewCount: PropTypes.number,
      reviewsCountLabel: PropTypes.string,
      badges: PropTypes.array,
      itemLabel: PropTypes.string,
      reviewable: PropTypes.bool,
      writeReviewAction: PropTypes.object,
      askQuestionAction: PropTypes.object,
      qAndAAction: PropTypes.object
    } ),
    PropTypes.shape( {
      /** Properties for SocialTags */
      id: PropTypes.string,
      type: PropTypes.string,
      items: PropTypes.array
    } ),
    PropTypes.shape( {
      /** Properties for MediaGallery */
      id: PropTypes.string,
      type: PropTypes.string,
      items: PropTypes.array.isRequired,
      nextAccessibility: PropTypes.string,
      previousAccessibility: PropTypes.string,
      ratio: PropTypes.string,
      tryItAction: PropTypes.object,
      carouselAccessibility: PropTypes.string,
      carouselDetailsAccessibility: PropTypes.string,
      carouselCardAccessibility: PropTypes.string,
      zoomLegendAccessibilityLabel: PropTypes.string,
      zoomPlusIconAccessibilityLabel: PropTypes.string,
      zoomMinusIconAccessibilityLabel: PropTypes.string,
      moveUpIconAccessibilityLabel: PropTypes.string,
      moveDownIconAccessibilityLabel: PropTypes.string,
      moveLeftIconAccessibilityLabel: PropTypes.string,
      moveRightIconAccessibilityLabel: PropTypes.string,
      screenReaderAccessibilityLabel: PropTypes.string,
      moveLegendAccessibilityLabel: PropTypes.string
    } ),
    PropTypes.shape( {
      /** Properties for ProductPricing */
      type: PropTypes.string,
      listPrice: PropTypes.string,
      originalPriceLabel: PropTypes.string,
      listPriceAccessibilityLabel: PropTypes.string,
      salePrice: PropTypes.string,
      salePriceAccessibilityLabel: PropTypes.string,
      kitPrice: PropTypes.string,
      compact: PropTypes.bool,
      xLarge: PropTypes.bool,
      message: PropTypes.string,
      priceLabel: PropTypes.string,
      offersLabel: PropTypes.string,
      offersAction: PropTypes.object,
      offers: PropTypes.array,
      title: PropTypes.shape( {
        text: PropTypes.string,
        htmlTag: PropTypes.string
      } ),
      productId: PropTypes.string,
      skuId: PropTypes.string,
      id: PropTypes.string,
      brandName: PropTypes.string,
      productName: PropTypes.string,
      variantTypeLabel: PropTypes.string,
      variantLabel: PropTypes.string,
      image: PropTypes.string,
      appDownloadLabel: PropTypes.string,
      appDownloadAction: PropTypes.object
    } ),
    PropTypes.shape( {
      /** Properties for ProductVariant */
      id: PropTypes.string,
      variants: PropTypes.array,
      typeLabel: PropTypes.string.isRequired,
      dimensionsLabel: PropTypes.string,
      dimensionsValue: PropTypes.string,
      shadeFinderAction: PropTypes.object,
      doneAction: PropTypes.object,
      closeAccessibilityAction: PropTypes.object,
      viewAllLabel: PropTypes.string,
      viewLessLabel: PropTypes.string,
      collapsible: PropTypes.bool,
      expanded: PropTypes.bool
    } ),
    PropTypes.shape( {
      /** Properties for ProductActionsV2 */
      id: PropTypes.string,
      displayState: PropTypes.string,
      bookmarkAction: PropTypes.shape( {
        label: PropTypes.string,
        graphql: PropTypes.string
      } ),
      removeBookmarkAction: PropTypes.shape( {
        label: PropTypes.string,
        url: PropTypes.string
      } ),
      bookmarked: PropTypes.bool,
      successLabel: PropTypes.string,
      inBagLabel: PropTypes.string,
      quantity: PropTypes.number,
      addedQuantity: PropTypes.number,
      commerceId: PropTypes.string,
      skuId: PropTypes.string,
      productId: PropTypes.string,
      viewItemsInOfferAction: PropTypes.shape( {
        label: PropTypes.string,
        graphql: PropTypes.string
      } ),
      comingSoon: PropTypes.shape( {
        comingSoonLabel: PropTypes.string,
        comingSoonMessage: PropTypes.string
      } ),
      notifyMe: PropTypes.shape( {
        email: PropTypes.bool,
        notifyMeMessage: PropTypes.string,
        notifyMeAction: PropTypes.shape( {
          label: PropTypes.string,
          graphql: PropTypes.string
        } ),
        notifyMeDisabled: PropTypes.bool
      } ),
      perk: PropTypes.shape( {
        perkLabel: PropTypes.string,
        perkMessage: PropTypes.string,
        perkAction: PropTypes.shape( {
          label: PropTypes.string,
          url: PropTypes.string
        } )
      } ),
      fulfillmentSection : PropTypes.shape( {
        fulfillmentType : PropTypes.string,
        icon : PropTypes.string,
        title : PropTypes.string,
        subtitle : PropTypes.string,
        tileState : PropTypes.string,
        addToBagAction: PropTypes.shape( {
          label: PropTypes.string,
          graphql: PropTypes.string
        } ),
        removeFromBagAction: PropTypes.shape( {
          label: PropTypes.string,
          graphql: PropTypes.string
        } ),
        buttonAction: PropTypes.shape( {
          label: PropTypes.string,
          graphql: PropTypes.string
        } ),
        deliveryInfo : PropTypes.shape( {
          location: PropTypes.string,
          speed: PropTypes.string,
          stockStatus: PropTypes.object,
          secondarySpeed: PropTypes.string,
          linkAction: PropTypes.shape( {
            label: PropTypes.string,
            graphql: PropTypes.string
          } )
        } ),
        maxQuantity: PropTypes.string,
        messages : PropTypes.array,
        selected: PropTypes.boolean,
        eligibilityState: PropTypes.string,
        updateVariantAction: PropTypes.shape( {
          label: PropTypes.string,
          graphql: PropTypes.string
        } ),
        selectAction: PropTypes.shape( {
          label: PropTypes.string,
          graphql: PropTypes.string
        } ),
        subscription: PropTypes.shape( {
          label: PropTypes.string,
          graphql: PropTypes.string
        } ),
        updateBagAction: PropTypes.shape( {
          label: PropTypes.string,
          graphql: PropTypes.string
        } )
      } )
    } ),
    PropTypes.shape( {
      /** Properties for ProductSummary */
      id: PropTypes.string,
      type: PropTypes.string,
      title: PropTypes.string,
      message: PropTypes.string
    } ),
    PropTypes.shape( {
      /** Properties for ProductDetail */
      id: PropTypes.string,
      description: PropTypes.string,
      descriptionAction: PropTypes.shape( {
        label: PropTypes.string
      } ),
      ingredients: PropTypes.string,
      ingredientsAction: PropTypes.shape( {
        label: PropTypes.string
      } ),
      restrictions: PropTypes.string,
      restrictionsAction: PropTypes.shape( {
        label: PropTypes.string
      } ),
      usage: PropTypes.string,
      usageAction: PropTypes.shape( {
        label: PropTypes.string
      } ),
      prop65WarningDisplay: PropTypes.bool,
      prop65WarningAction: PropTypes.shape( {
        label: PropTypes.string
      } ),
      prop65WarningDetail: PropTypes.string,
      prop65WarningLinkAction: PropTypes.shape( {
        label: PropTypes.string,
        url: PropTypes.string
      } )
    } )
  ),
  /** Properties for ProductHero */
  id: PropTypes.string,
  deviceType: PropTypes.string,
  ssr: PropTypes.bool
};

ProductHero.propTypes = propTypes;

export default ProductHero;
